// src/screens/s3/EarlierRetirement.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Earlier Retirement scenario
// Slider asks: what happens if you stop 2/3/5 years earlier? The chart and
// cards translate an altFreedomAge into "£ shortfall at alt age" and "extra
// monthly ISA contribution that closes the gap".
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import {
  ComposedChart, Area, Line, ReferenceLine,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { ClientData } from '../../types/ClientData';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

// Future value: lump + monthly contribution, annual rate, years.
function fv(balance: number, monthly: number, rate: number, years: number): number {
  if (years <= 0) return balance;
  const fvLump = balance * Math.pow(1 + rate, years);
  const fvMonthly = rate === 0
    ? monthly * 12 * years
    : monthly * 12 * (Math.pow(1 + rate, years) - 1) / rate;
  return fvLump + fvMonthly;
}

export default function EarlierRetirement({ data, onUpdate }: Props) {
  const clientAge = data.clientAge || 40;
  const currentFreedomAge = data.targetFreedomAge || 55;
  const minAlt = clientAge + 1;
  const maxAlt = Math.max(currentFreedomAge - 1, minAlt);
  const defaultAlt = Math.max(minAlt, currentFreedomAge - 3);

  const [altFreedomAge, setAltFreedomAge] = useState<number>(
    data.s3EarlierFreedomAge && data.s3EarlierFreedomAge >= minAlt && data.s3EarlierFreedomAge <= maxAlt
      ? data.s3EarlierFreedomAge
      : defaultAlt,
  );

  const freedomNumber = data.freedomNumberInflated || 0;

  // Monthly contributions
  const pensionMonthly =
    (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const isaMonthly = data.planIsaMonthlyContribution || data.isaContribution || 0;

  // Opening balances
  const startPension = data.pensionBalance || 0;
  const startISA = data.investmentsBalance || 0;
  const startCash =
    (data.efAccount1Balance || 0) +
    (data.efAccount2Balance || 0) +
    (data.savingsAccounts || []).reduce(
      (s, a: { balance?: number }) => s + (a.balance || 0),
      0,
    );

  const pensionRate = data.assumptionPensionGrowth || 0.08;
  const isaRate = data.assumptionIsaGrowth || 0.08;
  const cashRate = data.assumptionCashRate || 0.01;

  // Projection at altFreedomAge
  const yearsToAlt = Math.max(0, altFreedomAge - clientAge);
  const yearsToCurrent = Math.max(0, currentFreedomAge - clientAge);

  const potAtAlt =
    fv(startPension, pensionMonthly, pensionRate, yearsToAlt) +
    fv(startISA, isaMonthly, isaRate, yearsToAlt) +
    fv(startCash, 0, cashRate, yearsToAlt);

  const potAtCurrent =
    fv(startPension, pensionMonthly, pensionRate, yearsToCurrent) +
    fv(startISA, isaMonthly, isaRate, yearsToCurrent) +
    fv(startCash, 0, cashRate, yearsToCurrent);

  const shortfall = Math.max(0, freedomNumber - potAtAlt);

  // Extra monthly ISA needed to close the shortfall by altFreedomAge
  const annuityFactor =
    yearsToAlt <= 0 || isaRate === 0
      ? 12 * Math.max(yearsToAlt, 1)
      : 12 * (Math.pow(1 + isaRate, yearsToAlt) - 1) / isaRate;
  const extraMonthly = annuityFactor > 0 ? shortfall / annuityFactor : 0;

  const yearsEarlier = currentFreedomAge - altFreedomAge;

  // Chart data: pot grows with current plan AND pot grows with current plan +
  // extra monthly — so the "earlier plan" curve reaches Freedom Number at altAge.
  const chartData = useMemo(() => {
    const rows: Array<{ age: number; currentPlan: number; earlierPlan: number }> = [];
    for (let age = clientAge; age <= Math.max(currentFreedomAge, altFreedomAge) + 2; age++) {
      const y = age - clientAge;
      const current =
        fv(startPension, pensionMonthly, pensionRate, y) +
        fv(startISA, isaMonthly, isaRate, y) +
        fv(startCash, 0, cashRate, y);
      const earlier =
        fv(startPension, pensionMonthly, pensionRate, y) +
        fv(startISA, isaMonthly + extraMonthly, isaRate, y) +
        fv(startCash, 0, cashRate, y);
      rows.push({
        age,
        currentPlan: Math.round(current),
        earlierPlan: Math.round(earlier),
      });
    }
    return rows;
  }, [clientAge, currentFreedomAge, altFreedomAge, startPension, startISA, startCash,
      pensionMonthly, isaMonthly, pensionRate, isaRate, cashRate, extraMonthly]);

  const fmt = (n: number) => `£${Math.round(n).toLocaleString()}`;
  const fmtK = (n: number) => `£${Math.round(n / 1000)}k`;

  function handleSave() {
    // Persist the alternative age AND flip the scenario flag on so Programme
    // Summary / conditional nav pick it up.
    onUpdate({
      s3EarlierFreedomAge: altFreedomAge,
      s3ScenarioEarlierOn: true,
    });
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      {/* HEADER */}
      <h2 className="text-xl font-bold text-ma-navy mb-2">Earlier Retirement</h2>
      <p className="text-sm text-gray-600 mb-8">
        What if you stopped two, three, or five years earlier? Move the slider
        and watch what it would take.
      </p>

      {/* SLIDER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Alternative freedom age
          </label>
          <div className="text-3xl font-bold text-ma-navy">{altFreedomAge}</div>
        </div>
        <input
          type="range"
          min={minAlt}
          max={maxAlt}
          step={1}
          value={altFreedomAge}
          onChange={(e) => setAltFreedomAge(Number(e.target.value))}
          className="w-full accent-ma-navy"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{minAlt}</span>
          <span>Current plan: {currentFreedomAge}</span>
          <span>{maxAlt}</span>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          {yearsEarlier > 0
            ? `That's ${yearsEarlier} year${yearsEarlier === 1 ? '' : 's'} earlier than your current plan.`
            : yearsEarlier === 0
              ? 'Matches your current plan.'
              : 'Later than your current plan.'}
        </p>
      </div>

      {/* RESULT CARDS */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">
            Shortfall at age {altFreedomAge}
          </div>
          <div className="text-3xl font-bold text-amber-900">{fmt(shortfall)}</div>
          <p className="text-xs text-amber-800 mt-2">
            Pot projected at {fmt(potAtAlt)} vs Freedom Number of {fmt(freedomNumber)}.
          </p>
        </div>
        <div className="rounded-2xl border border-ma-navy/20 bg-ma-navy/5 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-ma-navy mb-1">
            Extra monthly to close it
          </div>
          <div className="text-3xl font-bold text-ma-navy">{fmt(extraMonthly)}</div>
          <p className="text-xs text-ma-navy/80 mt-2">
            Additional ISA contribution from today.
          </p>
        </div>
      </div>

      {/* CHART */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-3">
          Wealth trajectory
        </h3>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="age" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtK} stroke="#9CA3AF" />
              <Tooltip
                formatter={(v: any) => fmt(Number(v))}
                labelFormatter={(age) => `Age ${age}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                dataKey="currentPlan"
                name="Current plan"
                fill="#1E3A5F"
                fillOpacity={0.1}
                stroke="#1E3A5F"
                strokeWidth={2}
              />
              <Line
                dataKey="earlierPlan"
                name="Earlier plan"
                stroke="#10B981"
                strokeWidth={2}
                dot={false}
              />
              <ReferenceLine
                y={freedomNumber}
                stroke="#C9A84C"
                strokeDasharray="4 4"
                label={{ value: 'Freedom Number', fontSize: 10, fill: '#C9A84C', position: 'insideTopLeft' }}
              />
              <ReferenceLine
                x={altFreedomAge}
                stroke="#10B981"
                strokeDasharray="3 3"
                label={{ value: `Age ${altFreedomAge}`, fontSize: 10, position: 'top' }}
              />
              <ReferenceLine
                x={currentFreedomAge}
                stroke="#1E3A5F"
                strokeDasharray="3 3"
                label={{ value: `Age ${currentFreedomAge}`, fontSize: 10, position: 'top' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* PLAIN ENGLISH */}
      <div className="bg-ma-navy text-white rounded-2xl p-6 mb-6">
        {yearsEarlier > 0 ? (
          <p className="text-sm leading-relaxed">
            To stop at <strong>{altFreedomAge}</strong> instead of {currentFreedomAge},
            you'd need to find an extra <strong>{fmt(extraMonthly)}/month</strong> into
            an ISA from today. That's what closes the {fmt(shortfall)} gap. The trade-off
            is simple: pay now, reclaim {yearsEarlier} year{yearsEarlier === 1 ? '' : 's'} later.
          </p>
        ) : (
          <p className="text-sm leading-relaxed">
            At this age, your current plan already reaches (or exceeds) your Freedom
            Number — no additional contribution is required.
          </p>
        )}
      </div>

      {/* VOYANT BREAK */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-1">
          Voyant break — adviser check
        </div>
        <p className="text-sm text-blue-900 leading-relaxed">
          Re-run the plan in Voyant with retirement set to age {altFreedomAge} and an
          extra {fmt(extraMonthly)}/month into the ISA. Confirm the pot still crosses
          the Freedom Number and the plan is funded through 82.
        </p>
      </div>

      {/* SAVE */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-5 py-2.5 rounded-lg bg-ma-navy text-white text-sm font-semibold hover:bg-opacity-90 transition"
        >
          Save alternative age
        </button>
      </div>
    </div>
  );
}
